// ListScreen (TEMPLATES.md): pinned filters, optional strip, virtualized
// list, optional side column on desktop. Drawn through the kit (LOOKS.md
// Part 7): the rows come from `VirtualList`, the layout from classes.

import { useRef, type ReactNode } from 'react';
import { useTier } from '../shell/useTier';
import { EmptyState } from '../kit';
import { VirtualList } from '../kit/VirtualList';

export function ListScreen<T>({
  above,
  filters,
  strip,
  header,
  rows,
  renderRow,
  getKey,
  estimateSize = 56,
  side,
  empty,
  footer,
  body,
}: {
  /**
   * Controls set once and then left — which view, whose players — above the
   * filters: on a phone they scroll away and only the filters stick, which
   * took 42% of the screen when they all did (B-486).
   */
  above?: ReactNode;
  filters?: ReactNode;
  /**
   * Under the filters: on a phone it scrolls away above the list, and wider
   * it is the top of the list's own scroll rather than pinned over it, where
   * it left a desktop's list four rows (B-486).
   */
  strip?: ReactNode;
  header?: ReactNode;
  rows: T[];
  renderRow: (row: T, index: number) => ReactNode;
  getKey: (row: T) => string;
  estimateSize?: number;
  side?: ReactNode;
  empty?: ReactNode;
  /** Under the last row, in the list's own scroll: "Show more" for a paged list. */
  footer?: ReactNode;
  /**
   * The rows drawn some other way than one by one — a table that scrolls
   * sideways (Players' stats, B-412) — in the virtualized list's place while
   * there are rows; `rows` still says whether there are any.
   */
  body?: ReactNode;
}) {
  const tier = useTier();
  const wide = tier === 'desktop' || tier === 'large';
  const parentRef = useRef<HTMLElement>(null);
  // ON A PHONE THE SCREEN SCROLLS AS ONE (UX-38): the list is drawn against
  // the screen's scroll, not its own box's; the filters stick to the top and
  // the strip scrolls away.
  const screenRef = useRef<HTMLDivElement>(null);
  const phone = tier === 'phone';

  const list = (
    // THE LAST ROW HAS TO CLEAR THE CHAT CHIP. `.fsp-chat-chip` floats over the
    // bottom right of the content area on phone (x 306–376, y 740–778 at 390),
    // so the final row of any list sat under it however far you scrolled — Ken
    // hit it on Standings on 2026-09-22.
    <section className="fsp-region" aria-label="List" ref={parentRef}>
      {strip && !phone ? <div className="fsp-list-strip">{strip}</div> : null}
      {header ? <div className="fsp-region-head">{header}</div> : null}
      {rows.length === 0 ? (
        <EmptyState>{empty ?? 'Nothing here.'}</EmptyState>
      ) : body !== undefined ? (
        body
      ) : (
        <VirtualList
          // A new list when the scroller changes: it listens to the one it began with.
          key={phone ? 'screen' : 'region'}
          rows={rows}
          renderRow={renderRow}
          getKey={getKey}
          estimateSize={estimateSize}
          scroller={phone ? screenRef : parentRef}
        />
      )}
      {footer && rows.length > 0 ? <div className="fsp-list-footer">{footer}</div> : null}
      {/* A REAL ELEMENT, because neither of the two tidier ways works: growing
          the virtualized container only moves the scroll extent, and
          `padding-bottom` on a scroll container is ignored for overflowing
          content (both measured 2026-09-22). */}
      {tier === 'phone' && rows.length > 0 ? (
        <div className="fsp-list-chip-clearance" aria-hidden="true" />
      ) : null}
    </section>
  );

  return (
    <div className="fsp-screen fsp-flow" ref={screenRef}>
      {above ? <div className="fsp-pinned">{above}</div> : null}
      {filters ? <div className="fsp-pinned fsp-pinned-stick">{filters}</div> : null}
      {strip && phone ? <div className="fsp-pinned">{strip}</div> : null}
      <div className={`fsp-regions ${side && wide ? 'fsp-list-with-side' : 'fsp-list-alone'}`}>
        {list}
        {side && wide ? (
          <section className="fsp-region" aria-label="Side">
            {side}
          </section>
        ) : null}
      </div>
    </div>
  );
}
